import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

export type ResultSession = 'open' | 'close';
export type SettlementSession = ResultSession | 'full';
export type SettlementStatus = 'running' | 'completed' | 'failed';

export interface MarketResult {
  id: number;
  market_id: number | null;
  market_name: string;
  result_date: string;
  open_pana: string | null;
  open_digit: string | null;
  close_pana: string | null;
  close_digit: string | null;
  jodi_digit: string | null;
  callback_type: 'delhi_market' | 'maharashtra_market' | string;
  synced_to_market: boolean;
  received_at: string | null;
}

export interface SettlementRun {
  id: number;
  market_id: number;
  market_name: string;
  session: SettlementSession;
  settlement_date: string;
  total_bets: number;
  winning_bets: number;
  losing_bets: number;
  cancelled_bets: number;
  total_payout: string;
  status: SettlementStatus;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface ResultFilters {
  market_id: string;
  market_name: string;
  result_date: string;
  date_from: string;
  date_to: string;
  search: string;
  page: number;
  page_size: number;
  limit: number;
}

export interface SettlementFilters {
  market_id: string;
  session: '' | SettlementSession;
  settlement_date: string;
  status: '' | SettlementStatus;
  limit: number;
}

export interface DeclareResultPayload {
  market_id: number;
  result_date: string;
  pana: string;
  digit?: string;
  callback_type?: 'delhi_market' | 'maharashtra_market';
}

export interface RevertResultPayload {
  market_id: number;
  result_date: string;
  callback_type?: 'delhi_market' | 'maharashtra_market';
  confirm: true;
}

export interface WinnerPreviewPayload {
  market_id: number;
  result_date: string;
  session: ResultSession;
  pana: string;
  digit?: string;
  callback_type?: 'delhi_market' | 'maharashtra_market';
  winner_limit?: number;
}

export interface WinnerPreviewItem {
  bet_id: number;
  user_id: number | null;
  user_name: string;
  mobile_number: string | null;
  market_id: number;
  market_name: string | null;
  session: string;
  bet_type_id: number | null;
  bet_type_name: string | null;
  selected_number: string | null;
  amount: string;
  estimated_payout: string;
  placed_at: string | null;
}

export interface WinnerPreviewData {
  market_id: number;
  market_name: string;
  result_date: string;
  session: ResultSession;
  pana: string;
  digit: string;
  callback_type: 'delhi_market' | 'maharashtra_market' | string;
  total_bets: number;
  winning_bets: number;
  losing_bets: number;
  total_stake: string;
  winning_stake: string;
  estimated_payout: string;
  winner_count_returned: number;
  winner_limit: number;
  winners_truncated: boolean;
  winners: WinnerPreviewItem[];
}

interface WinnerPreviewResponse {
  success: boolean;
  message: string;
  data: WinnerPreviewData;
}


interface RevertResultResponse {
  success: boolean;
  message: string;
  data: unknown;
}

export interface DeclarationData {
  created: boolean;
  unchanged: boolean;
  session: ResultSession;
  calculated_digit?: string;
  settlement_queued?: boolean;
  result: MarketResult;
}

interface ResultListResponse {
  success: boolean;
  count: number;
  total_pages: number;
  current_page: number;
  page_size: number;
  results: MarketResult[];
}

interface SettlementListResponse {
  success: boolean;
  count: number;
  settlement_runs: SettlementRun[];
}

interface DeclarationResponse {
  success: boolean;
  message: string;
  data: DeclarationData;
}

export interface ResultState {
  results: MarketResult[];
  settlementRuns: SettlementRun[];
  selectedResult: MarketResult | null;
  lastDeclaration: DeclarationData | null;
  winnerPreview: WinnerPreviewData | null;
  resultFilters: ResultFilters;
  settlementFilters: SettlementFilters;
  resultsCount: number;
  resultsTotalPages: number;
  resultsCurrentPage: number;
  resultsPageSize: number;
  resultsLoading: boolean;
  settlementsLoading: boolean;
  declaring: boolean;
  previewingWinners: boolean;
  revertingOpen: boolean;
  revertingClose: boolean;
  error: string | null;
  success: string | null;
}

const initialState: ResultState = {
  results: [],
  settlementRuns: [],
  selectedResult: null,
  lastDeclaration: null,
  winnerPreview: null,
  resultFilters: {
    market_id: '',
    market_name: '',
    result_date: '',
    date_from: '',
    date_to: '',
    search: '',
    page: 1,
    page_size: 10,
    limit: 100,
  },
  settlementFilters: {
    market_id: '',
    session: '',
    settlement_date: '',
    status: '',
    limit: 100,
  },
  resultsCount: 0,
  resultsTotalPages: 1,
  resultsCurrentPage: 1,
  resultsPageSize: 10,
  resultsLoading: false,
  settlementsLoading: false,
  declaring: false,
  previewingWinners: false,
  revertingOpen: false,
  revertingClose: false,
  error: null,
  success: null,
};

const getErrorMessage = (error: any, fallback: string): string => {
  const responseData = error?.response?.data;

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData;
  }

  if (typeof responseData?.error === 'string') {
    return responseData.error;
  }

  if (typeof responseData?.detail === 'string') {
    return responseData.detail;
  }

  if (typeof responseData?.message === 'string') {
    return responseData.message;
  }

  if (responseData?.errors) {
    try {
      return JSON.stringify(responseData.errors);
    } catch {
      return fallback;
    }
  }

  return error?.message || fallback;
};

const buildQueryString = (
  params: Record<string, string | number | undefined>
): string => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      queryParams.append(key, String(value));
    }
  });

  const query = queryParams.toString();
  return query ? `?${query}` : '';
};

export const fetchResults = createAsyncThunk(
  'result/fetchResults',
  async (
    params: Partial<ResultFilters> = {},
    { rejectWithValue }
  ) => {
    try {
      const query = buildQueryString({
        market_id: params.market_id,
        market_name: params.market_name,
        result_date: params.result_date,
        date_from: params.date_from,
        date_to: params.date_to,
        search: params.search,
        page: params.page,
        page_size: params.page_size,
        limit: params.limit,
      });

      const response = await api.get<ResultListResponse>(
        `/admin/results/${query}`
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to load market results.')
      );
    }
  }
);

export const fetchSettlementRuns = createAsyncThunk(
  'result/fetchSettlementRuns',
  async (
    params: Partial<SettlementFilters> = {},
    { rejectWithValue }
  ) => {
    try {
      const query = buildQueryString({
        market_id: params.market_id,
        session: params.session,
        settlement_date: params.settlement_date,
        status: params.status,
        limit: params.limit,
      });

      const response = await api.get<SettlementListResponse>(
        `/admin/results/settlements/${query}`
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to load settlement runs.')
      );
    }
  }
);

export const previewWinners = createAsyncThunk(
  'result/previewWinners',
  async (payload: WinnerPreviewPayload, { rejectWithValue }) => {
    try {
      const response = await api.post<WinnerPreviewResponse>(
        '/admin/results/winners/preview/',
        payload
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to preview winners.')
      );
    }
  }
);

export const declareOpenResult = createAsyncThunk(
  'result/declareOpenResult',
  async (payload: DeclareResultPayload, { rejectWithValue }) => {
    try {
      const response = await api.post<DeclarationResponse>(
        '/admin/results/open/',
        payload
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to declare the open result.')
      );
    }
  }
);

export const declareCloseResult = createAsyncThunk(
  'result/declareCloseResult',
  async (payload: DeclareResultPayload, { rejectWithValue }) => {
    try {
      const response = await api.post<DeclarationResponse>(
        '/admin/results/close/',
        payload
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to declare the close result.')
      );
    }
  }
);


export const revertOpenResult = createAsyncThunk(
  'result/revertOpenResult',
  async (payload: RevertResultPayload, { rejectWithValue }) => {
    try {
      const response = await api.post<RevertResultResponse>(
        '/admin/results/revert/open/',
        payload
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to revert the open result.')
      );
    }
  }
);

export const revertCloseResult = createAsyncThunk(
  'result/revertCloseResult',
  async (payload: RevertResultPayload, { rejectWithValue }) => {
    try {
      const response = await api.post<RevertResultResponse>(
        '/admin/results/revert/close/',
        payload
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to revert the close result.')
      );
    }
  }
);

const upsertResult = (results: MarketResult[], result: MarketResult) => {
  const existingIndex = results.findIndex(
    (item) =>
      item.id === result.id ||
      (item.market_name.toLowerCase() === result.market_name.toLowerCase() &&
        item.result_date === result.result_date &&
        item.callback_type === result.callback_type)
  );

  if (existingIndex >= 0) {
    results[existingIndex] = result;
  } else {
    results.unshift(result);
  }

  results.sort((a, b) => {
    const dateComparison = b.result_date.localeCompare(a.result_date);
    if (dateComparison !== 0) return dateComparison;

    return (b.received_at || '').localeCompare(a.received_at || '');
  });
};

const resultSlice = createSlice({
  name: 'result',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    clearLastDeclaration: (state) => {
      state.lastDeclaration = null;
    },
    clearWinnerPreview: (state) => {
      state.winnerPreview = null;
    },
    setSelectedResult: (
      state,
      action: PayloadAction<MarketResult | null>
    ) => {
      state.selectedResult = action.payload;
    },
    setResultFilters: (
      state,
      action: PayloadAction<Partial<ResultFilters>>
    ) => {
      state.resultFilters = {
        ...state.resultFilters,
        ...action.payload,
      };
    },
    setSettlementFilters: (
      state,
      action: PayloadAction<Partial<SettlementFilters>>
    ) => {
      state.settlementFilters = {
        ...state.settlementFilters,
        ...action.payload,
      };
    },
    resetResultFilters: (state) => {
      state.resultFilters = initialState.resultFilters;
    },
    resetSettlementFilters: (state) => {
      state.settlementFilters = initialState.settlementFilters;
    },
    resetResultState: (state) => {
      state.resultsLoading = false;
      state.settlementsLoading = false;
      state.declaring = false;
      state.previewingWinners = false;
      state.revertingOpen = false;
      state.revertingClose = false;
      state.winnerPreview = null;
      state.error = null;
      state.success = null;
      state.selectedResult = null;
      state.lastDeclaration = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchResults.pending, (state) => {
        state.resultsLoading = true;
        state.error = null;
      })
      .addCase(fetchResults.fulfilled, (state, action) => {
        state.resultsLoading = false;
        state.results = action.payload.results || [];
        state.resultsCount = action.payload.count || 0;
        state.resultsTotalPages = action.payload.total_pages || 1;
        state.resultsCurrentPage = action.payload.current_page || 1;
        state.resultsPageSize = action.payload.page_size || 10;
      })
      .addCase(fetchResults.rejected, (state, action) => {
        state.resultsLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchSettlementRuns.pending, (state) => {
        state.settlementsLoading = true;
        state.error = null;
      })
      .addCase(fetchSettlementRuns.fulfilled, (state, action) => {
        state.settlementsLoading = false;
        state.settlementRuns = action.payload.settlement_runs || [];
      })
      .addCase(fetchSettlementRuns.rejected, (state, action) => {
        state.settlementsLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(previewWinners.pending, (state) => {
        state.previewingWinners = true;
        state.winnerPreview = null;
        state.error = null;
      })
      .addCase(previewWinners.fulfilled, (state, action) => {
        state.previewingWinners = false;
        state.winnerPreview = action.payload.data;
      })
      .addCase(previewWinners.rejected, (state, action) => {
        state.previewingWinners = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(declareOpenResult.pending, (state) => {
        state.declaring = true;
        state.error = null;
        state.success = null;
      })
      .addCase(declareOpenResult.fulfilled, (state, action) => {
        state.declaring = false;
        state.success =
          action.payload.message || 'Open result declared successfully.';
        state.lastDeclaration = action.payload.data;
        upsertResult(state.results, action.payload.data.result);
      })
      .addCase(declareOpenResult.rejected, (state, action) => {
        state.declaring = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(declareCloseResult.pending, (state) => {
        state.declaring = true;
        state.error = null;
        state.success = null;
      })
      .addCase(declareCloseResult.fulfilled, (state, action) => {
        state.declaring = false;
        state.success =
          action.payload.message || 'Close result declared successfully.';
        state.lastDeclaration = action.payload.data;
        upsertResult(state.results, action.payload.data.result);
      })
      .addCase(declareCloseResult.rejected, (state, action) => {
        state.declaring = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(revertOpenResult.pending, (state) => {
        state.revertingOpen = true;
        state.error = null;
        state.success = null;
      })
      .addCase(revertOpenResult.fulfilled, (state, action) => {
        state.revertingOpen = false;
        state.success =
          action.payload.message || 'Open bids reverted successfully.';
        state.lastDeclaration = null;
      })
      .addCase(revertOpenResult.rejected, (state, action) => {
        state.revertingOpen = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(revertCloseResult.pending, (state) => {
        state.revertingClose = true;
        state.error = null;
        state.success = null;
      })
      .addCase(revertCloseResult.fulfilled, (state, action) => {
        state.revertingClose = false;
        state.success =
          action.payload.message || 'Close bids reverted successfully.';
        state.lastDeclaration = null;
      })
      .addCase(revertCloseResult.rejected, (state, action) => {
        state.revertingClose = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  clearLastDeclaration,
  clearWinnerPreview,
  setSelectedResult,
  setResultFilters,
  setSettlementFilters,
  resetResultFilters,
  resetSettlementFilters,
  resetResultState,
} = resultSlice.actions;

export default resultSlice.reducer;
